import { CalendarRemove01Icon, Clock01Icon, Moon02Icon, Sun03Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatLongDate, formatTime, formatTimezone } from '../../lib/format.js';

export type AgentAvailability = 'open' | 'closed' | 'always_open' | 'not_configured';

export interface AgentStatusProps {
  availability: AgentAvailability;
  /** ISO time of the next opening (when closed) or closing (when open). */
  nextChangeAt?: string | null;
  timezone?: string | null;
  /** e.g. "WhatsApp · Torre Alameda" */
  scopeLabel?: string;
  /** Configure action shown when not configured. */
  action?: React.ReactNode;
  className?: string;
}

/** Whether the AI agent is operating now, with the next opening/closing time in the business timezone. */
export const AgentStatus = React.forwardRef<HTMLDivElement, AgentStatusProps>(function AgentStatus(
  { availability, nextChangeAt, timezone, scopeLabel, action, className },
  ref,
) {
  const tz = timezone ?? 'America/Mexico_City';
  const when = nextChangeAt ? `${formatLongDate(nextChangeAt, tz)}, ${formatTime(nextChangeAt, tz)}` : null;
  const config = {
    open: {
      icon: <Icon icon={Sun03Icon} aria-hidden />,
      iconClass: 'bg-success-bg text-success',
      title: 'Agente activo ahora',
      detail: when ? `Cierra el ${when}` : 'En horario de operación',
    },
    always_open: {
      icon: <Icon icon={Clock01Icon} aria-hidden />,
      iconClass: 'bg-success-bg text-success',
      title: 'Agente activo las 24 horas',
      detail: 'Responde en cualquier momento',
    },
    closed: {
      icon: <Icon icon={Moon02Icon} aria-hidden />,
      iconClass: 'bg-warning-bg text-warning',
      title: 'Fuera de horario',
      detail: when ? `Vuelve a responder el ${when}` : 'Sin próxima apertura programada',
    },
    not_configured: {
      icon: <Icon icon={CalendarRemove01Icon} aria-hidden />,
      iconClass: 'bg-danger-bg text-danger',
      title: 'Horario sin configurar',
      detail: 'La IA no responde hasta que se guarde un horario.',
    },
  }[availability];
  return (
    <div ref={ref} role="status" className={cn('flex items-start gap-3 rounded-card border border-border-subtle bg-surface-default p-4', className)}>
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-pill [&_svg]:size-[18px]', config.iconClass)}>{config.icon}</span>
      <div className="grid min-w-0 flex-1 gap-0.5">
        <p className="text-body font-semibold text-text-primary">{config.title}</p>
        <p className="text-body text-text-secondary">{config.detail}</p>
        <p className="text-meta text-text-secondary">
          {[scopeLabel, availability !== 'not_configured' && timezone ? formatTimezone(timezone, nextChangeAt ?? undefined) : null].filter(Boolean).join(' · ')}
        </p>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
});
